import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useTranslation } from "react-i18next";
import { Application } from "../applications/Application";
import { ApplicationFormatter } from "../applications/ApplicationProvider";
import { Company } from "../companies/Company";
import { Role } from "./Role";
import { User } from "./User";
import { isAdmin } from "./AccountServiceRoles";
import { formatApplicationRole } from "./ApplicationRoles";

// All filter labels are forced shrunk so they sit above the field even when
// empty; the color is themed globally via MuiFormLabel in theme.ts.
const shrunkLabelProps = { shrink: true };

export interface UsersListFilters {
  applicationNames: string[];
  applicationRoles: string[];
  companyIds: string[];
  search?: string;
}

export const emptyUsersListFilters: UsersListFilters = {
  applicationNames: [],
  applicationRoles: [],
  companyIds: [],
};

export function filtersToQueryString(filters: UsersListFilters): string {
  const parts: string[] = [];
  filters.applicationNames.forEach(name => parts.push(`applicationNames=${name}`));
  filters.applicationRoles.forEach(role => parts.push(`applicationRoles=${role}`));
  filters.companyIds.forEach(id => parts.push(`company=${id}`));
  if (filters.search) parts.push(`search=${filters.search}`);
  return parts.join('&');
}

interface RoleOption {
  app: Application;
  role: Role;
}

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

export function UsersListFiltersBar(props: {
  applications: Application[];
  companies: Company[];
  account?: User;
  applicationFormatter: ApplicationFormatter;
  onFiltersChange: (filters: UsersListFilters) => void;
}) {
  const { applications, companies, account, applicationFormatter, onFiltersChange } = props;
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedApps, setSelectedApps] = useState<Application[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    onFiltersChange({
      applicationNames: selectedApps.map(app => app.name),
      applicationRoles: selectedRoles.map(({ app, role }) => `${app.name}.${role.name}`),
      companyIds: selectedCompanies.map(c => c.id!).filter(Boolean),
      search: debouncedSearch.trim() || undefined,
    });
  }, [selectedApps, selectedRoles, selectedCompanies, debouncedSearch, onFiltersChange]);

  const sortedApps = useMemo(() =>
    [...applications].sort((a, b) =>
      applicationFormatter.formatApplication(a.name).localeCompare(applicationFormatter.formatApplication(b.name))
    ), [applications, applicationFormatter]);

  const sortedRoles = useMemo<RoleOption[]>(() => {
    const all = applications.flatMap(app => app.roles.map(role => ({ app, role })));
    return all.sort((a, b) =>
      formatApplicationRole(a.app, a.role.name).localeCompare(formatApplicationRole(b.app, b.role.name))
    );
  }, [applications]);

  const sortedCompanies = useMemo(() =>
    [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies]);

  const showRoleFilter = !!account && isAdmin(account);

  const hasActiveFilter =
    !!searchInput ||
    selectedApps.length > 0 ||
    selectedRoles.length > 0 ||
    selectedCompanies.length > 0;

  const handleReset = () => {
    setSearchInput('');
    setSelectedApps([]);
    setSelectedRoles([]);
    setSelectedCompanies([]);
  };

  return (
    <Grid container={true} justifyContent="center" sx={{ marginTop: 2, marginBottom: 1 }}>
      <Grid item={true} xs={12}>
        <Grid container={true} spacing={2} alignItems="flex-start">
          <Grid item={true} xs={12} sm={6} md={true}>
            <TextField
              fullWidth
              size="small"
              label={t('buttons.search') as string}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              InputLabelProps={shrunkLabelProps}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item={true} xs={12} sm={6} md={true}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={sortedApps}
              value={selectedApps}
              onChange={(_, value) => setSelectedApps(value)}
              getOptionLabel={app => applicationFormatter.formatApplication(app.name)}
              isOptionEqualToValue={(a, b) => a.name === b.name}
              renderOption={(optionProps, app, { selected }) => (
                <li {...optionProps}>
                  <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {applicationFormatter.formatApplication(app.name)}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label={t('Application.title') as string} InputLabelProps={shrunkLabelProps} />
              )}
            />
          </Grid>
          {showRoleFilter && (
            <Grid item={true} xs={12} sm={6} md={true}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={sortedRoles}
                value={selectedRoles}
                onChange={(_, value) => setSelectedRoles(value)}
                getOptionLabel={({ app, role }) => formatApplicationRole(app, role.name)}
                isOptionEqualToValue={(a, b) => a.app.name === b.app.name && a.role.name === b.role.name}
                renderOption={(optionProps, option, { selected }) => (
                  <li {...optionProps}>
                    <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {formatApplicationRole(option.app, option.role.name)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" label={t('User.role') as string} InputLabelProps={shrunkLabelProps} />
                )}
              />
            </Grid>
          )}
          <Grid item={true} xs={12} sm={6} md={true}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={sortedCompanies}
              value={selectedCompanies}
              onChange={(_, value) => setSelectedCompanies(value)}
              getOptionLabel={c => c.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(optionProps, company, { selected }) => (
                <li {...optionProps}>
                  <Checkbox icon={checkboxIcon} checkedIcon={checkboxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {company.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label={t('Company.titleEdit') as string} InputLabelProps={shrunkLabelProps} />
              )}
            />
          </Grid>
          <Grid item={true} xs="auto" sx={{ pt: 0.5 }}>
            <Tooltip title={t('buttons.resetFilters') as string}>
              <span>
                <IconButton onClick={handleReset} disabled={!hasActiveFilter}>
                  <FilterAltOffIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
